import LoadingAnimation from "src/components/blocks/LoadingAnimation.js";
import useAuth from "./UseAuth.js";
import { createContext, useEffect } from "react";
import { GA4_MEASUREMENT_ID } from "./gtag.js";
import { isEmbeddedPath } from "./Utils.js";

export const AuthContext = createContext();

// Must match the gtag id injected into apps/stanza-react-app/index.html.
// Setting it as a user property via `gtag('config', GA4_ID, { user_id })` is
// the only path GA4 recognizes — passing `user_id` in event-payload data
// (the previous pattern) gets silently stripped because GA4 reserves the
// parameter name. Once set here, the built-in `userId` dimension becomes
// queryable via the Reporting API for every event the user fires.
function AuthProviderApp({ children, isEmbedded }) {
  const {
    user,
    isLoading,
    isCheckingRedirect,
    refetchAuth,
    handleSignOut,
    checkSignIn,
    pendingEmailLink,
    confirmEmailLinkSignIn,
    dismissEmailLink,
    redirectAccountExistsError,
    clearRedirectAccountExistsError,
  } = useAuth({
    isEmbedded,
  });

  // Wire signed-in user into GA4's built-in user-property slot. Keyed on
  // Mongo `_id` (stable across the user's lifetime; not personally
  // identifying on its own). Cleared on sign-out so post-logout events
  // aren't attributed to the previous user in the same session.
  // Mirrored to localStorage so the inline gtag('set') in index.html
  // can populate user_id on the first page_view of the next load.
  useEffect(() => {
    const uid = user?._id ? String(user._id) : null;
    try {
      if (uid) window.localStorage.setItem("stanza_uid", uid);
      else window.localStorage.removeItem("stanza_uid");
    } catch (e) { /* private mode */ }
    if (!window.gtag) return;
    if (uid) {
      window.gtag("config", GA4_MEASUREMENT_ID, { user_id: uid });
    } else {
      // GA4 canonical clear path. `config(..., { user_id: undefined })`
      // is not guaranteed to override a value previously set via
      // `gtag('set', ...)` in the inline index.html script.
      window.gtag("set", { user_id: null });
    }
  }, [user?._id]);

  return isLoading ? (
    <LoadingAnimation />
  ) : (
    <AuthContext.Provider
      value={{
        user,
        handleSignOut,
        refetchAuth,
        checkSignIn,
        isLoading,
        isCheckingRedirect,
        pendingEmailLink,
        confirmEmailLinkSignIn,
        dismissEmailLink,
        redirectAccountExistsError,
        clearRedirectAccountExistsError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Mocked AuthProvider for embedded views, don't need to check auth
function AuthProviderEmbedded({ children }) {
  const defaultValues = {
    user: null,
    handleSignOut: () => { },
    refetchAuth: () => { },
    checkSignIn: () => { },
    isLoading: false,
  };
  const { user, isLoading, refetchAuth, handleSignOut, checkSignIn } =
    defaultValues;
  return (
    <AuthContext.Provider
      value={{ user, handleSignOut, refetchAuth, checkSignIn, isLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const AuthProvider = (props) => {
  if (!props.isEmbedded && !isEmbeddedPath(window.location)) {
    return <AuthProviderApp {...props} />;
  } else {
    return <AuthProviderEmbedded {...props} />;
  }
};
